import React from 'react';
import { StageBadge } from '../components/StageBadge';
import { formatAmount, formatCurrency, type Lead } from './mockData';
import type { Loan } from './loanData';
export type FieldGroup = 'Identity' | 'Loan' | 'Contact' | 'Activity' | 'Pipeline' | 'Conditions';
export type FieldDef<T> = {
  id: string;
  label: string;
  group: FieldGroup;
  value: (item: T) => string;
  render?: (item: T) => React.ReactNode;
};
export const leadFields: FieldDef<Lead>[] = [{
  id: 'stage',
  label: 'Stage',
  group: 'Pipeline',
  value: lead => lead.stage,
  render: lead => <StageBadge stage={lead.stage} data-id="element-748" data-mp="1aw3sds76jpna" />
}, {
  id: 'amount',
  label: 'Loan amount',
  group: 'Loan',
  value: lead => formatAmount(lead.amount)
}, {
  id: 'state',
  label: 'State',
  group: 'Identity',
  value: lead => lead.state
}, {
  id: 'source',
  label: 'Lead source',
  group: 'Identity',
  value: lead => lead.source ?? '—'
}, {
  id: 'loanPurpose',
  label: 'Loan purpose',
  group: 'Loan',
  value: lead => lead.loanPurpose ?? '—'
}, {
  id: 'phone',
  label: 'Phone',
  group: 'Contact',
  value: lead => lead.phone ?? '—'
}, {
  id: 'email',
  label: 'Email',
  group: 'Contact',
  value: lead => lead.email ?? '—'
}, {
  id: 'lastTouch',
  label: 'Last touch',
  group: 'Activity',
  value: lead => lead.lastTouch ?? '—'
}, {
  id: 'attempts',
  label: 'Call cadence',
  group: 'Activity',
  value: lead => lead.attempts ?? '—'
}, {
  id: 'detail',
  label: 'Next step',
  group: 'Activity',
  value: lead => lead.detail
}, {
  id: 'openTasks',
  label: 'Open tasks',
  group: 'Activity',
  value: lead => String((lead.tasks ?? []).filter(task => !task.done).length)
}];
export const loanFields: FieldDef<Loan>[] = [{
  id: 'number',
  label: 'Loan number',
  group: 'Identity',
  value: loan => loan.number
}, {
  id: 'stage',
  label: 'Stage',
  group: 'Pipeline',
  value: loan => loan.stage
}, {
  id: 'amount',
  label: 'Loan amount',
  group: 'Loan',
  value: loan => formatCurrency(loan.amount)
}, {
  id: 'state',
  label: 'State',
  group: 'Identity',
  value: loan => loan.state
}, {
  id: 'processor',
  label: 'Processor',
  group: 'Pipeline',
  value: loan => loan.processor
}, {
  id: 'closingDate',
  label: 'Target closing',
  group: 'Pipeline',
  value: loan => loan.closingDate
}, {
  id: 'daysInStage',
  label: 'Days in stage',
  group: 'Pipeline',
  value: loan => `${loan.daysInStage}d`
}, {
  id: 'stalled',
  label: 'Stalled',
  group: 'Pipeline',
  value: loan => loan.stalled ? `Yes · ${loan.daysInStage}d` : 'No'
}, {
  id: 'outstanding',
  label: 'Conditions outstanding',
  group: 'Conditions',
  value: loan => String(loan.conditions.filter(condition => condition.status === 'Outstanding').length)
}, {
  id: 'awaitingReview',
  label: 'Docs awaiting review',
  group: 'Conditions',
  value: loan => String(loan.conditions.filter(condition => condition.status === 'AI Matched').length)
}, {
  id: 'cleared',
  label: 'Conditions cleared',
  group: 'Conditions',
  value: loan => String(loan.conditions.filter(condition => condition.status === 'Accepted').length)
}, {
  id: 'openTasks',
  label: 'Open tasks',
  group: 'Activity',
  value: loan => String(loan.tasks.filter(task => !task.done).length)
}];
export const defaultLeadFieldIds = ['amount', 'detail', 'stage'];
export const defaultLoanFieldIds = ['number', 'amount', 'stage', 'processor', 'awaitingReview', 'closingDate'];